import { useState, useCallback } from "react";
import { DocumentTemplate, useFlowsTemplates } from "../../../../stores/flowsTemplatesStore";

export interface UseTemplatesTableReturn {
  // Menu state
  anchorEl: HTMLElement | null;
  selectedTemplateId: string | null;
  selectedTemplate: DocumentTemplate | undefined;

  // Dialogs state
  uploadDialogOpen: boolean;
  pdfEditorOpen: boolean;
  pdfEditorTemplateId: string | null;
  confirmDeleteOpen: boolean;
  editModalOpen: boolean;
  editingTemplate: DocumentTemplate | null;
  deleteLoading: boolean;

  // Computed values
  isPdfFile: boolean;

  // Menu handlers
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, templateId: string) => void;
  handleMenuClose: () => void;

  // Action handlers
  handleUpload: () => void;
  handleEditPlaceholders: () => void;
  handleClosePdfEditor: () => void;
  handleOpenEditModal: (template: DocumentTemplate) => void;
  handleCloseEditModal: () => void;
  handleOpenDeleteConfirm: (templateId: string) => void;
  handleCloseDeleteConfirm: () => void;
  handleConfirmDelete: () => Promise<void>;
  handleCloseUploadDialog: () => void;
}

export function useTemplatesTable(templates: DocumentTemplate[]): UseTemplatesTableReturn {
  const { deleteTemplate } = useFlowsTemplates();

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Dialogs state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false);
  const [pdfEditorTemplateId, setPdfEditorTemplateId] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Computed values
  const selectedTemplate = templates.find(t => t.identifier === selectedTemplateId);
  const isPdfFile = selectedTemplate?.file_name?.toLowerCase().endsWith('.pdf') ?? false;

  // Menu handlers
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, templateId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplateId(templateId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Upload handlers
  const handleUpload = useCallback(() => {
    setUploadDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleCloseUploadDialog = useCallback(() => {
    setUploadDialogOpen(false);
    setSelectedTemplateId(null);
  }, []);

  // PDF Editor handlers
  const handleEditPlaceholders = useCallback(() => {
    if (selectedTemplateId) {
      setPdfEditorTemplateId(selectedTemplateId);
      setPdfEditorOpen(true);
    }
    handleMenuClose();
  }, [selectedTemplateId, handleMenuClose]);

  const handleClosePdfEditor = useCallback(() => {
    setPdfEditorOpen(false);
    setPdfEditorTemplateId(null);
  }, []);

  // Edit Modal handlers
  const handleOpenEditModal = useCallback((template: DocumentTemplate) => {
    setEditingTemplate(template);
    setEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingTemplate(null);
  }, []);

  // Delete handlers
  const handleOpenDeleteConfirm = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    setConfirmDeleteOpen(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setConfirmDeleteOpen(false);
    setSelectedTemplateId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (selectedTemplateId) {
      setDeleteLoading(true);
      await deleteTemplate(selectedTemplateId);
      setDeleteLoading(false);
    }
    handleCloseDeleteConfirm();
  }, [selectedTemplateId, deleteTemplate, handleCloseDeleteConfirm]);

  return {
    anchorEl,
    selectedTemplateId,
    selectedTemplate,
    uploadDialogOpen,
    pdfEditorOpen,
    pdfEditorTemplateId,
    confirmDeleteOpen,
    editModalOpen,
    editingTemplate,
    deleteLoading,
    isPdfFile,
    handleMenuOpen,
    handleMenuClose,
    handleUpload,
    handleEditPlaceholders,
    handleClosePdfEditor,
    handleOpenEditModal,
    handleCloseEditModal,
    handleOpenDeleteConfirm,
    handleCloseDeleteConfirm,
    handleConfirmDelete,
    handleCloseUploadDialog,
  };
}
